import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  ArrowPathIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import TenantDetails from './TenantDetails';

const TenantDirectory = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    fetchTenantMetrics();
    const interval = setInterval(fetchTenantMetrics, 10 * 60 * 1000); // 10 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchTenantMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/platform/tenants/metrics');
      setTenants(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching tenant metrics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tenant.status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-blue-100 text-blue-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthIcon = (score) => {
    if (score >= 80) return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    if (score >= 60) return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
    return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) return (mb / 1024).toFixed(1) + ' GB';
    return mb.toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="flex items-center space-x-3">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchTenantMetrics}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Tenants</dt>
                  <dd className="text-lg font-medium text-gray-900">{tenants.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Tenants</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {tenants.filter(t => t.status === 'ACTIVE').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">U</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(tenants.reduce((sum, t) => sum + (t.metrics?.totalUsers || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">A</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users (24h)</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(tenants.reduce((sum, t) => sum + (t.metrics?.activeUsersToday || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Users
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Business
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                  Emails
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden xl:table-cell">
                  System
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Health
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="ml-2 text-gray-600">Loading tenant metrics...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No tenants found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {tenant.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {tenant.subdomain}
                        </div>
                        <span className={`inline-flex px-1.5 py-0.5 text-xs rounded-full ${getStatusColor(tenant.status)}`}>
                          {tenant.status}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div className="space-y-0.5">
                        <div>Total: <span className="font-medium">{tenant.metrics?.totalUsers || 0}</span></div>
                        <div>Active: <span className="font-medium text-green-600">{tenant.metrics?.activeUsersToday || 0}</span></div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div className="space-y-0.5">
                        <div>Items: <span className="font-medium">{tenant.metrics?.totalItems || 0}</span></div>
                        <div>Services: <span className="font-medium">{tenant.metrics?.totalServices || 0}</span></div>
                        <div>Active: <span className="font-medium text-blue-600">{tenant.metrics?.activeReservations || 0}</span></div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs hidden lg:table-cell">
                      <div className="space-y-0.5">
                        <div>Today: <span className="font-medium">{tenant.metrics?.emailsToday || 0}</span></div>
                        <div>Month: <span className="font-medium">{tenant.metrics?.emailsMonth || 0}</span></div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs hidden xl:table-cell">
                      <div className="space-y-0.5">
                        <div>{formatSize(tenant.metrics?.databaseSize)}</div>
                        <div className={`${(tenant.metrics?.errorRate || 0) > 1 ? 'text-red-600' : 'text-green-600'}`}>
                          {(tenant.metrics?.errorRate || 0).toFixed(1)}% errors
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs">
                        <div className="flex items-center space-x-1">
                          {getHealthIcon(tenant.metrics?.healthScore || 0)}
                          <span className="font-medium">{tenant.metrics?.healthScore || 0}%</span>
                        </div>
                        {tenant.metrics?.issues > 0 && (
                          <div className="text-xs text-red-600 mt-0.5">
                            {tenant.metrics.issues} issue{tenant.metrics.issues !== 1 ? 's' : ''}
                          </div>
                        )}
                        {tenant.metrics?.healthIssues && tenant.metrics.healthIssues.length > 0 && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                              View details
                            </summary>
                            <div className="mt-1 space-y-1">
                              {tenant.metrics.healthIssues.map((issue, index) => (
                                <div key={index} className={`text-xs px-1 py-0.5 rounded ${
                                  issue.severity === 'high' ? 'bg-red-100 text-red-700' : 
                                  issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  <div className="font-medium">{issue.message}</div>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedTenant(tenant)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Details Modal */}
      {selectedTenant && (
        <TenantDetails 
          tenant={selectedTenant} 
          onClose={() => setSelectedTenant(null)} 
        />
      )}
    </div>
  );
};

export default TenantDirectory;